import { PLUGIN_CHANNELS } from "@brains/contracts";
import type {
  Plugin,
  PluginType,
  PluginCapabilities,
  Tool,
  Resource,
  ToolContext,
} from "./interfaces";
import type { MessageHandler, MessageSender } from "@brains/messaging-service";
import type { IShell } from "./interfaces";
import { getErrorMessage } from "@brains/utils/error";
import { ConsoleLogger, type Logger } from "@brains/utils/logger";
import {
  CallbackProgressReporter,
  type ProgressNotification,
  type ProgressReporter,
} from "@brains/utils/progress";
import type { UserPermissionLevel } from "@brains/templates";
import { actorRefSchema } from "@brains/contracts";
import { z } from "@brains/utils/zod";
import { type PluginConfigSchema, PluginConfigValidationError } from "./config";

// Message schemas for validation
const toolExecuteRequestSchema = z.object({
  toolName: z.string(),
  args: z.unknown(),
  progressToken: z.union([z.string(), z.number()]).optional(),
  hasProgress: z.boolean().optional(),
  interfaceType: z.string(),
  actor: actorRefSchema,
  displayName: z.string().optional(),
  conversationId: z.string().optional(),
  channelId: z.string().optional(),
  channelName: z.string().optional(),
  runId: z.string().optional(),
  toolCallId: z.string().optional(),
  userPermissionLevel: z.enum(["admin", "trusted", "public"]).optional(),
  isAnchor: z.boolean().optional(),
  signal: z
    .custom<AbortSignal>((value) => value instanceof AbortSignal)
    .optional(),
});

const resourceGetRequestSchema = z.object({
  resourceUri: z.string(),
});

// Core context interface - minimal requirements for all plugins
export interface CoreContext {
  pluginId: string;
  logger: Logger;
  messaging: {
    send: MessageSender;
    subscribe: <T = unknown, R = unknown>(
      channel: string,
      handler: MessageHandler<T, R>,
    ) => () => void;
  };
}

/**
 * Base abstract class for plugins that provides common functionality
 */
export abstract class BasePlugin<
  TConfig,
  TConfigInput,
  TContext extends CoreContext = CoreContext,
> implements Plugin {
  public readonly id: string;
  public readonly version: string;
  public readonly description: string;
  public readonly packageName: string;
  public abstract readonly type: PluginType;
  protected config: TConfig;
  protected context?: TContext;

  /**
   * Get the logger - uses context logger if available, creates temp logger otherwise
   */
  protected get logger(): Logger {
    return (
      this.context?.logger ?? ConsoleLogger.createFresh({ context: this.id })
    );
  }

  constructor(
    id: string,
    packageJson: { name: string; version: string; description?: string },
    partialConfig: TConfigInput,
    configSchema: PluginConfigSchema<TConfig>,
  ) {
    this.id = id;
    this.packageName = packageJson.name;
    this.version = packageJson.version;
    this.description = packageJson.description ?? `${packageJson.name} plugin`;

    // Let Zod schema handle defaults during parsing
    const parsedConfig = configSchema.safeParse(partialConfig);
    if (!parsedConfig.success) {
      throw new PluginConfigValidationError(
        id,
        parsedConfig.error.issues.map((issue) => ({
          path: issue.path.map(String).join("."),
          code: issue.code,
          message: issue.message,
        })),
      );
    }
    this.config = parsedConfig.data;
  }

  /**
   * Register the plugin - must be overridden by typed base classes
   * to create appropriate context from shell
   */
  abstract register(shell: IShell): Promise<PluginCapabilities>;

  /**
   * Set up message handlers for tool and resource execution
   */
  protected setupMessageHandlers(context: TContext): void {
    // Subscribe to tool execution requests for this specific plugin
    context.messaging.subscribe(
      PLUGIN_CHANNELS.toolExecute(this.id),
      async (message) => {
        try {
          // Validate and parse the message payload
          const parsedRequest = toolExecuteRequestSchema.safeParse(
            message.payload,
          );
          if (!parsedRequest.success) {
            return {
              success: false,
              error: "Invalid tool execution request format",
            };
          }

          const {
            toolName,
            args,
            progressToken,
            hasProgress,
            interfaceType,
            actor,
            displayName,
            conversationId,
            channelId,
            channelName,
            runId,
            toolCallId,
            userPermissionLevel,
            isAnchor,
            signal,
          } = parsedRequest.data;

          const tools = await this.getTools();
          const tool = tools.find((t) => t.name === toolName);

          if (!tool) {
            return {
              success: false,
              error: `Tool not found: ${toolName}`,
            };
          }

          // Create context with routing metadata and optional progress callback
          const toolContext: ToolContext = {
            interfaceType,
            actor,
            ...(displayName && { displayName }),
            ...(conversationId && { conversationId }),
            ...(channelId && { channelId }),
            ...(channelName && { channelName }),
            ...(runId && { runId }),
            ...(toolCallId && { toolCallId }),
            ...(userPermissionLevel && { userPermissionLevel }),
            ...(isAnchor !== undefined && { isAnchor }),
            ...(signal && { signal }),
            ...(hasProgress &&
              progressToken !== undefined && {
                progressToken,
                sendProgress: async (
                  notification: ProgressNotification,
                ): Promise<void> => {
                  // Send progress notification back through message bus
                  await context.messaging.send({
                    type: PLUGIN_CHANNELS.progress(this.id),
                    payload: {
                      progressToken,
                      notification,
                    },
                  });
                },
              }),
          };

          // Response shape is validated at the consumer boundary
          // (MCPService registry wrapper / mcp-registration protocol path).
          const result = await tool.handler(args, toolContext);
          return {
            success: true,
            data: result,
          };
        } catch (error) {
          this.logger.error("Tool execution error", error);
          return {
            success: false,
            error: getErrorMessage(error),
          };
        }
      },
    );

    // Subscribe to resource get requests for this specific plugin
    context.messaging.subscribe(
      PLUGIN_CHANNELS.resourceGet(this.id),
      async (message) => {
        try {
          // Validate and parse the message payload
          const parsedRequest = resourceGetRequestSchema.safeParse(
            message.payload,
          );
          if (!parsedRequest.success) {
            return {
              success: false,
              error: "Invalid resource get request format",
            };
          }
          const { resourceUri } = parsedRequest.data;

          const resources = await this.getResources();
          const resource = resources.find((r) => r.uri === resourceUri);

          if (!resource) {
            return {
              success: false,
              error: `Resource not found: ${resourceUri}`,
            };
          }

          // Get the resource
          const result = await resource.handler();
          return {
            success: true,
            data: result,
          };
        } catch (error) {
          this.logger.error("Resource fetch error", error);
          return {
            success: false,
            error: getErrorMessage(error),
          };
        }
      },
    );
  }

  /**
   * Lifecycle hook called during registration.
   * Override this for capability registration and subscriptions that must exist
   * before the system-wide "all plugins registered" signal.
   */
  protected async onRegister(_context: TContext): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Lifecycle hook called after every plugin has registered but before initial
   * content discovery. Override for composition finalization that must precede
   * imported entity validation.
   */
  protected async onRegistrationComplete(_context: TContext): Promise<void> {
    // Default implementation does nothing
  }

  /** Dispatch the post-registration lifecycle hook. */
  public async finalizeRegistration(): Promise<void> {
    await this.onRegistrationComplete(this.getContext());
  }

  /**
   * Lifecycle hook called after all plugins have registered and the shell has
   * completed ready-state preparation.
   * Override this for work that reads identity/profile or depends on other
   * plugins having completed registration.
   */
  protected async onReady(_context: TContext): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Dispatch the ready lifecycle hook.
   */
  public async ready(): Promise<void> {
    await this.onReady(this.getContext());
  }

  /**
   * Build plugin capabilities from overridable capability hooks.
   */
  protected async getCapabilities(): Promise<PluginCapabilities> {
    const instructions = await this.getInstructions();
    return {
      tools: await this.getTools(),
      resources: await this.getResources(),
      ...(instructions && { instructions }),
    };
  }

  /**
   * Get the tools provided by this plugin
   * Override this to provide plugin-specific tools
   */
  protected async getTools(): Promise<Tool[]> {
    return [];
  }

  /**
   * Get the resources provided by this plugin
   * Override this to provide plugin-specific resources
   */
  protected async getResources(): Promise<Resource[]> {
    return [];
  }

  /**
   * Get behavioral instructions for the agent's system prompt
   * Override this to provide plugin-specific agent guidance
   */
  protected async getInstructions(): Promise<string | undefined> {
    return undefined;
  }

  /**
   * Optional shutdown method for cleanup
   * Override this if your plugin needs cleanup
   */
  async shutdown?(): Promise<void> {
    await this.onShutdown();
  }

  /**
   * Lifecycle hook called during shutdown
   * Override this to perform cleanup
   */
  protected async onShutdown(): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Get the plugin context
   */
  protected getContext(): TContext {
    if (!this.context) {
      throw new Error(`Plugin ${this.id}: Plugin not registered yet`);
    }
    return this.context;
  }

  /**
   * Determine the permission level for a user in this plugin's context
   * Must be implemented by plugins that handle user permissions
   * Default implementation returns 'public' for safety
   */
  public determineUserPermissionLevel(_userId: string): UserPermissionLevel {
    return "public";
  }

  /**
   * Create a progress bridge that converts job queue ProgressReporter to MCP progress events
   * Used by job-based tools to report progress via MCP progressToken
   */
  protected createProgressBridge(
    progressToken?: string | number,
  ): ProgressReporter | undefined {
    if (!progressToken) return undefined;

    const context = this.context;
    if (!context) return undefined;

    const pluginId = this.id;
    return CallbackProgressReporter.from(
      async (notification: ProgressNotification) => {
        await context.messaging.send({
          type: PLUGIN_CHANNELS.progress(pluginId),
          payload: {
            progressToken,
            notification: {
              progress: notification.progress,
              total: notification.total,
              message: notification.message,
            },
          },
        });
      },
    );
  }
}
